<script setup lang="ts">
import { toRef } from 'vue'
import { usePassportBuilderLinks, type RelationRow } from '../model/usePassportBuilderLinks'
import type { ObjectTypeFieldsResponse } from '../model/objectTypeFields'
import { useConfirm, VIcons } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import { WObjectSubTypeSelect } from '@/widgets'

const props = defineProps<{
  fromTypeId: number
  refetchFn: () => Promise<unknown>
  objectTypeFieldsResponse: ObjectTypeFieldsResponse
  objectTypeFieldsLoading: boolean
}>()

const modelValue = defineModel<boolean>({ required: true })

const { show } = useConfirm()
const { trs } = useTranslate()
const link = usePassportBuilderLinks(
  toRef(props, 'fromTypeId'),
  props.refetchFn,
  toRef(props, 'objectTypeFieldsResponse'),
  modelValue,
)

function close() {
  modelValue.value = false
}

function confirmDelete(row: RelationRow) {
  const name =
    row.name.trim() || trs('itf.passport_builder_link_fallback_name', { id: row.toTypeId })
  show({
    title: trs('itf.passport_builder_link_delete_confirm', { name }),
    confirmFunc: async () => {
      await link.deleteRow(row.id)
    },
  })
}

function rowTargetDisabled(row: RelationRow): boolean {
  return row.id > 0
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    attach="body"
    :max-width="1100"
    scrollable
    @update:model-value="modelValue = $event"
  >
    <VCard class="pb-links-modal__card">
      <VCardTitle class="d-flex align-center pa-4 pb-links-modal__header">
        <span>{{ $t('itf.passport_builder_links_title') }}</span>
        <VSpacer />
        <VBtn icon variant="text" density="compact" @click="close">
          <span aria-hidden="true">✕</span>
        </VBtn>
      </VCardTitle>

      <VCardText class="pa-4 pt-0 pb-links-modal__body">
        <!-- Форма добавления связи — наверху, всегда видна -->
        <div class="text-subtitle-2 mt-2 mb-2">{{ $t('itf.passport_builder_links_add') }}</div>
        <div class="d-flex align-center pb-links-row mb-4">
          <VTextField
            v-model="link.draft.value.name"
            :label="$t('itf.passport_builder_link_name')"
            hide-details
            density="compact"
            class="flex-grow-1"
          />
          <VTextField
            v-model="link.draft.value.reverseName"
            :label="$t('itf.passport_builder_link_reverse_name')"
            hide-details
            density="compact"
            class="flex-grow-1"
          />
          <WObjectSubTypeSelect
            v-model="link.draft.value.toTypeId"
            :label="$t('itf.passport_builder_link_target_type')"
            hide-details
            dense
            :exclude-sub-type-ids="[props.fromTypeId]"
            class="flex-grow-1"
          />
          <VSelect
            v-model="link.draft.value.cardinality"
            :items="link.referenceTypeItems.value"
            :label="$t('itf.passport_builder_link_cardinality')"
            hide-details
            density="compact"
            class="flex-shrink-0"
            style="max-width: 200px"
          />
          <VCheckbox
            v-model="link.draft.value.isRequired"
            :label="$t('itf.required')"
            hide-details
            density="compact"
            class="flex-shrink-0"
          />
          <VBtn
            color="primary"
            size="small"
            density="compact"
            variant="tonal"
            :loading="link.saving.value"
            @click="link.createRow()"
          >
            <VIcons type="add" />
          </VBtn>
        </div>

        <VDivider class="my-4" />

        <div v-if="props.objectTypeFieldsLoading || link.loading.value" class="text-center pa-8">
          <VProgressCircular indeterminate />
        </div>

        <div v-else-if="link.isEmpty.value" class="text-center pa-8 text-medium-emphasis">
          {{ $t('itf.passport_builder_links_empty') }}
        </div>

        <div v-else class="pb-links-grid">
          <div
            v-for="row in link.rows.value"
            :key="row.id"
            class="d-flex align-center pb-links-row mb-4"
          >
            <VTextField
              v-model="row.name"
              :label="$t('itf.passport_builder_link_name')"
              hide-details
              density="compact"
              class="flex-grow-1"
            />
            <VTextField
              v-model="row.reverseName"
              :label="$t('itf.passport_builder_link_reverse_name')"
              hide-details
              density="compact"
              class="flex-grow-1"
            />
            <WObjectSubTypeSelect
              v-model="row.toTypeId"
              :label="$t('itf.passport_builder_link_target_type')"
              hide-details
              dense
              :disabled="rowTargetDisabled(row)"
              class="flex-grow-1"
            />
            <VSelect
              v-model="row.cardinality"
              :items="link.referenceTypeItems.value"
              :label="$t('itf.passport_builder_link_cardinality')"
              hide-details
              density="compact"
              class="flex-shrink-0"
              style="max-width: 200px"
            />
            <VCheckbox
              v-model="row.isRequired"
              :label="$t('itf.required')"
              hide-details
              density="compact"
              class="flex-shrink-0"
            />
            <VBtn
              color="success"
              size="small"
              density="compact"
              variant="tonal"
              :loading="link.saving.value"
              @click="link.saveRow(row)"
            >
              <VIcons type="check" />
            </VBtn>
            <VBtn
              icon
              color="error"
              size="small"
              density="compact"
              variant="text"
              @click="confirmDelete(row)"
            >
              <VIcons type="delete" />
            </VBtn>
          </div>
        </div>
      </VCardText>

      <VCardActions class="pa-4 pt-0 pb-links-modal__actions">
        <VSpacer />
        <VBtn variant="tonal" @click="close">{{ $t('itf.cancel') }}</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.pb-links-modal__card {
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pb-links-modal__header {
  flex: none;
}

.pb-links-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.pb-links-modal__actions {
  flex: none;
}

.pb-links-row {
  gap: 12px;
}

.pb-links-row :deep(.v-btn) {
  height: 40px;
}
</style>
